/* eslint-disable react/prop-types */
import React, { memo, useState, useEffect } from 'react';
import { TouchableOpacity, View, Linking, Platform, Alert } from 'react-native';
import { QText } from './index';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '../hooks/MakeStylesHook';
import FileIcon from './Icons/FileIcon';
import BusinessIcon from './Icons/BusinessIcon';
import CalendarIcon from './Icons/CalendarIcon';
import UniversalFileViewer from './UniversalFileViewer';
import SecureFileOpener from './SecureFileOpener';
import UniversalAttachmentPreview from './UniversalAttachmentPreview';
import { showDateTime } from '../utils';

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    marginBottom: 16,
    padding: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: theme.colors.outline + '10',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  cardHeader: {
    backgroundColor: theme.colors.primary + '08',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.outline + '15',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  businessSection: {
    flex: 1,
  },
  businessInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  businessImage: {
    width: 40,
    height: 40,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: theme.colors.primary + '15',
  },
  businessImagePlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  businessImageText: {
    color: theme.colors.surface,
    fontSize: 16,
    fontWeight: 'bold',
  },
  businessDetails: {
    flex: 1,
  },
  businessName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.secondaryText,
    marginBottom: 2,
  },
  businessEmail: {
    fontSize: 12,
    color: theme.colors.placeHolderText,
  },
  tokenContainer: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 50,
  },
  tokenNumber: {
    color: theme.colors.surface,
    fontSize: 18,
    fontWeight: 'bold',
  },
  tokenLabel: {
    color: theme.colors.surface + 'CC',
    fontSize: 10,
    marginTop: 2,
  },
  cardBody: {
    padding: 16,
  },
  queueSection: {
    marginBottom: 20,
  },
  queueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.secondaryText,
    flex: 1,
  },
  queueDetails: {
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: theme.colors.outline + '20',
  },
  queueName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.secondaryText,
    marginBottom: 8,
  },
  queueInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  queueInfoLabel: {
    fontSize: 12,
    color: theme.colors.placeHolderText,
    minWidth: 60,
  },
  queueInfoValue: {
    fontSize: 12,
    color: theme.colors.secondaryText,
    fontWeight: '500',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  attachmentsSection: {
    marginTop: 4,
  },
  attachmentsList: {
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: theme.colors.outline + '20',
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 6,
    marginBottom: 8,
  },
  attachmentItemLast: {
    marginBottom: 0,
  },
  attachmentIcon: {
    marginRight: 12,
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.secondaryText,
    marginBottom: 2,
  },
  attachmentSize: {
    fontSize: 11,
    color: theme.colors.placeHolderText,
  },
  attachmentPreview: {
    width: 32,
    height: 32,
    borderRadius: 4,
    marginLeft: 8,
  },
  attachmentCount: {
    backgroundColor: theme.colors.primary + '15',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  attachmentCountText: {
    fontSize: 11,
    color: theme.colors.primary,
    fontWeight: '600',
  },
  expandButton: {
    alignItems: 'center',
    paddingVertical: 8,
    marginTop: 8,
  },
  expandButtonText: {
    fontSize: 12,
    color: theme.colors.primary,
    fontWeight: '600',
  },
}));

const AttachmentQueueCard = ({ queueData }) => {
  const styles = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [secureFileRequest, setSecureFileRequest] = useState(null);
  const [expandedAttachments, setExpandedAttachments] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Size unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    const statusColors = {
      Confirmed: theme.colors.success || '#4CAF50',
      Unconfirmed: theme.colors.warning || '#FF9800',
      Completed: theme.colors.info || '#2196F3',
      Cancelled: theme.colors.error || '#F44336',
    };
    return statusColors[status] || theme.colors.placeHolderText;
  };

  const getBusinessInitials = (name) => {
    if (!name) return 'B';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const handleFilePreview = (file) => {
    // Use universal file viewer for all file types
    setCurrentFile(file);
    setShowFileViewer(true);
  };

  const openFile = async (file) => {
    // With the new simplified approach, we just need the attachment ID
    const attachmentId = file?.id;

    if (!attachmentId) {
      Alert.alert(
        'Cannot Open File',
        'File ID is missing. Please try refreshing the page.',
        [{ text: 'OK' }]
      );
      return;
    }

    try {
      // Set the secure file request with just the attachment ID
      setSecureFileRequest({ attachmentId });
    } catch (error) {
      console.error('Error opening secure file:', error);
      Alert.alert(
        'Error Opening File',
        'Failed to open the file. Please try again or contact support if the issue persists.',
        [{ text: 'OK' }]
      );
    }
  };

  const attachments = queueData.attachments || [];
  const displayAttachments = expandedAttachments
    ? attachments
    : attachments.slice(0, 2);

  return (
    <View style={styles.card}>
      {/* Header Section */}
      <View style={styles.cardHeader}>
        <View style={styles.headerRow}>
          <View style={styles.businessSection}>
            <View style={styles.businessInfo}>
              {queueData.business?.profile_image_url ? (
                <Image
                  source={{ uri: queueData.business.profile_image_url }}
                  style={styles.businessImage}
                  onError={() => {}}
                />
              ) : (
                <View style={styles.businessImagePlaceholder}>
                  <QText style={styles.businessImageText}>
                    {getBusinessInitials(queueData.business?.name)}
                  </QText>
                </View>
              )}
              <View style={styles.businessDetails}>
                <QText style={styles.businessName}>
                  {queueData.business?.name || 'Unknown Business'}
                </QText>
                <QText style={styles.businessEmail}>
                  {queueData.business?.email || 'No email provided'}
                </QText>
              </View>
            </View>
          </View>
          <View style={styles.tokenContainer}>
            <QText style={styles.tokenNumber}>#{queueData.token_number}</QText>
            <QText style={styles.tokenLabel}>TOKEN</QText>
          </View>
        </View>
      </View>

      {/* Body Section */}
      <View style={styles.cardBody}>
        {/* Queue Information */}
        <View style={styles.queueSection}>
          <View style={styles.queueHeader}>
            <QText style={styles.sectionTitle}>{t('queue_information')}</QText>
          </View>
          <View style={styles.queueDetails}>
            <QText style={styles.queueName}>{queueData.queue?.name}</QText>
            <View style={styles.queueInfoRow}>
              <QText style={styles.queueInfoLabel}>{t('status')}:</QText>
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: getStatusColor(queueData.status) + '20' },
                ]}
              >
                <QText
                  style={[
                    styles.statusText,
                    { color: getStatusColor(queueData.status) },
                  ]}
                >
                  {queueData.status}
                </QText>
              </View>
            </View>
            {queueData.booking_datetime && (
              <View style={styles.queueInfoRow}>
                <QText style={styles.queueInfoLabel}>{t('booking')}:</QText>
                <QText style={styles.queueInfoValue}>
                  {showDateTime(queueData.booking_datetime)}
                </QText>
              </View>
            )}
            {queueData.queue?.timezone && (
              <View style={styles.queueInfoRow}>
                <QText style={styles.queueInfoLabel}>{t('timezone')}:</QText>
                <QText style={styles.queueInfoValue}>
                  {queueData.queue.timezone}
                </QText>
              </View>
            )}
          </View>
        </View>

        {/* Attachments Section */}
        {attachments.length > 0 && (
          <View style={styles.attachmentsSection}>
            <View style={styles.queueHeader}>
              <QText style={styles.sectionTitle}>{t('attachments')}</QText>
              <View style={styles.attachmentCount}>
                <QText style={styles.attachmentCountText}>
                  {attachments.length}{' '}
                  {attachments.length === 1 ? 'file' : 'files'}
                </QText>
              </View>
            </View>
            <View style={styles.attachmentsList}>
              {displayAttachments.map((file, index) => (
                <TouchableOpacity
                  key={file.id || index}
                  style={[
                    styles.attachmentItem,
                    index === displayAttachments.length - 1 &&
                      styles.attachmentItemLast,
                  ]}
                  onPress={() => handleFilePreview(file)}
                >
                  <View style={styles.attachmentIcon}>
                    <FileIcon
                      width={20}
                      height={20}
                      color={theme.colors.primary}
                    />
                  </View>
                  <View style={styles.attachmentInfo}>
                    <QText style={styles.attachmentName} numberOfLines={1}>
                      {file.name}
                    </QText>
                    <QText style={styles.attachmentSize}>
                      {formatFileSize(file.size)}
                    </QText>
                  </View>
                  <UniversalAttachmentPreview
                    attachment={file}
                    style={styles.attachmentPreview}
                    onPress={() => handleFilePreview(file)}
                  />
                </TouchableOpacity>
              ))}

              {attachments.length > 2 && (
                <TouchableOpacity
                  style={styles.expandButton}
                  onPress={() => setExpandedAttachments(!expandedAttachments)}
                >
                  <QText style={styles.expandButtonText}>
                    {expandedAttachments
                      ? t('show_less')
                      : t('show_more_files', { count: attachments.length - 2 })}
                  </QText>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}
      </View>

      {/* Universal File Viewer */}
      <UniversalFileViewer
        visible={showFileViewer}
        onClose={() => setShowFileViewer(false)}
        attachment={currentFile}
      />

      {/* Secure File Opener */}
      {secureFileRequest && (
        <SecureFileOpener
          attachmentId={secureFileRequest.attachmentId}
          onComplete={() => setSecureFileRequest(null)}
        />
      )}
    </View>
  );
};

export default memo(AttachmentQueueCard);
